import { estimatePasswordStrength, type PasswordStrength } from './passwordStrength';

export interface PasswordStrengthMeterProps {
  password: string;
}

const LABELS: Record<PasswordStrength, string> = {
  weak: 'Faible',
  medium: 'Moyen',
  strong: 'Fort',
};

// FND-20 : couleur par niveau de force — rouge/ambre/vert brand, cohérent avec
// la sémantique déjà utilisée pour formErrorClassName() (styles.ts).
// UX-64 : `medium` était `text-amber-600` — échoue WCAG AA sur fond blanc
// (3.19:1, ce texte `text-xs` était une vraie régression d'accessibilité
// pré-existante) — corrigé en `amber-800` (7.09:1 sur blanc), même teinte de
// texte ambre unique retenue partout ailleurs par UX-64.
const STRENGTH_STYLES: Record<PasswordStrength, string> = {
  weak: 'text-red-600',
  medium: 'text-amber-800',
  strong: 'text-brand-700',
};

// Retour visuel simple (texte + couleur via classe CSS), pas de barre animée ni de
// dépendance tierce — voir passwordStrength.ts pour la justification du choix.
// Rendu conditionnel (rien tant que le champ est vide) : évite d'afficher "Faible"
// avant même que l'utilisateur ait commencé à taper.
// `ss-password-strength(--${strength})` PRÉSERVÉS tels quels — mêmes raisons que
// `ss-form-error`/`ss-button--${variant}` (voir styles.ts/Button.tsx) : aucun
// test actuel ne les vérifie, mais gardés par cohérence de convention.
export function PasswordStrengthMeter({ password }: PasswordStrengthMeterProps) {
  if (!password) return null;

  const strength = estimatePasswordStrength(password);
  return (
    <p
      className={`ss-password-strength ss-password-strength--${strength} text-xs font-medium ${STRENGTH_STYLES[strength]}`}
      role="status"
    >
      Force du mot de passe : {LABELS[strength]}
    </p>
  );
}
